import React from 'react';
import { urlDoArquivo } from '../../services/upload';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Edit, Trash2, DollarSign, ArrowLeft, Calendar, MapPin, User, Car } from 'lucide-react';
import { despesasService } from '../../services/despesas';
import { useConfirm, usePrompt } from '../../components/ConfirmDialog';
import { formatCurrency, formatDate, formatExpenseCategory, formatExpenseStatus, formatExpenseType, formatPaymentMethod, formatKilometers } from '../../utils/formatters';

const DespesaDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const showPrompt = usePrompt();

  const { data, isLoading, error } = useQuery({
    queryKey: ['despesa', id],
    queryFn: () => despesasService.getDespesa(Number(id)),
    enabled: Boolean(id)
  });

  const deleteMutation = useMutation({
    mutationFn: () => despesasService.deleteDespesa(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['despesas'] });
      toast.success('Despesa excluída com sucesso!');
      navigate('/despesas');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir despesa');
    }
  });

  const markAsPaidMutation = useMutation({
    mutationFn: (formaPagamento: string) => despesasService.marcarComoPaga(Number(id), formaPagamento),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['despesa', id] });
      toast.success('Despesa marcada como paga!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao marcar despesa como paga');
    }
  });

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Excluir despesa',
      message: 'Tem certeza que deseja excluir esta despesa? Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir',
      variant: 'danger',
    });
    if (ok) {
      deleteMutation.mutate();
    }
  };

  const handleMarkAsPaid = async () => {
    const formaPagamento = await showPrompt({
      title: 'Marcar como paga',
      message: 'Selecione a forma de pagamento:',
      confirmLabel: 'Confirmar',
      variant: 'primary',
      input: {
        type: 'select',
        label: 'Forma de pagamento',
        defaultValue: 'pix',
        options: [
          { value: 'pix', label: 'PIX' },
          { value: 'credito', label: 'Cartão de Crédito' },
          { value: 'debito', label: 'Cartão de Débito' },
          { value: 'dinheiro', label: 'Dinheiro' },
        ],
      },
    });
    if (formaPagamento) {
      markAsPaidMutation.mutate(formaPagamento);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">Erro ao carregar despesa</p>
        <button 
          onClick={() => navigate('/despesas')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Voltar para lista
        </button>
      </div>
    );
  }

  const despesa = data.data;

  const getStatusBadge = (status: string) => {
    const variants = {
      paga: 'bg-green-100 text-green-800',
      pendente: 'bg-yellow-100 text-yellow-800',
      vencida: 'bg-red-100 text-red-800',
      cancelada: 'bg-gray-100 text-gray-800'
    } as const;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800'}`}>
        {formatExpenseStatus(status)}
      </span>
    );
  };

  const getCategoriaBadge = (categoria: string) => {
    const colors = {
      combustivel: 'bg-blue-100 text-blue-800',
      manutencao: 'bg-orange-100 text-orange-800',
      seguro: 'bg-green-100 text-green-800',
      ipva: 'bg-purple-100 text-purple-800',
      licenciamento: 'bg-yellow-100 text-yellow-800',
      multa: 'bg-red-100 text-red-800',
      pedagio: 'bg-indigo-100 text-indigo-800',
      estacionamento: 'bg-pink-100 text-pink-800',
      lavagem: 'bg-cyan-100 text-cyan-800',
      financiamento: 'bg-gray-100 text-gray-800',
      depreciacao: 'bg-slate-100 text-slate-800',
      outros: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[categoria as keyof typeof colors] || colors.outros}`}>
        {formatExpenseCategory(categoria)}
      </span>
    );
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/despesas')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar para lista
        </button>
        
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{despesa.descricao}</h1>
            <p className="text-gray-600">{despesa.fornecedor}</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate(`/despesas/${id}/editar`)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Edit className="w-4 h-4 mr-1" />
              Editar
            </button>
            {despesa.status !== 'paga' && (
              <button
                onClick={handleMarkAsPaid}
                disabled={markAsPaidMutation.isPending}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                <DollarSign className="w-4 h-4 mr-1" />
                Marcar como Paga
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Excluir
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações Principais */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Informações da Despesa</h2>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Valor</dt>
                <dd className="mt-1 text-sm text-gray-900 font-semibold">
                  {formatCurrency(despesa.valor)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">
                  {getStatusBadge(despesa.status)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Categoria</dt>
                <dd className="mt-1">
                  {getCategoriaBadge(despesa.categoria)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Tipo</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatExpenseType(despesa.tipo)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Data da Despesa</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(despesa.data_despesa)}
                </dd>
              </div>
              {despesa.data_vencimento && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Data de Vencimento</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(despesa.data_vencimento)}
                  </dd>
                </div>
              )}
              {despesa.data_pagamento && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Data de Pagamento</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(despesa.data_pagamento)}
                  </dd>
                </div>
              )}
              {despesa.forma_pagamento && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Forma de Pagamento</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatPaymentMethod(despesa.forma_pagamento)}
                  </dd>
                </div>
              )}
              {despesa.numero_documento && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Número do Documento</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {despesa.numero_documento}
                  </dd>
                </div>
              )}
              {despesa.quilometragem && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Quilometragem</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatKilometers(despesa.quilometragem)}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Parcelamento */}
          {despesa.total_parcelas && despesa.total_parcelas > 1 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Parcelamento</h2>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Parcela Atual</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {despesa.parcela_atual} de {despesa.total_parcelas}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Valor da Parcela</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatCurrency(despesa.valor_parcela || 0)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Valor Total</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatCurrency(despesa.valor)}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {/* Observações */}
          {despesa.observacoes && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Observações</h2>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{despesa.observacoes}</p>
            </div>
          )}

          {/* Comprovantes */}
          {(despesa.comprovantes?.length ?? 0) > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Comprovantes ({despesa.comprovantes!.length})
              </h2>
              <ul className="space-y-2">
                {despesa.comprovantes!.map((caminho) => (
                  <li key={caminho} className="flex items-center justify-between gap-3 border-b border-gray-100 pb-2 last:border-0">
                    <span className="text-sm text-gray-700 truncate" title={caminho}>
                      {caminho.split('/').pop()}
                    </span>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <a href={urlDoArquivo(caminho)} target="_blank" rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline text-sm">Ver</a>
                      <span className="text-gray-400">|</span>
                      <a href={urlDoArquivo(caminho)} download
                        className="text-green-600 hover:text-green-800 underline text-sm">Download</a>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Veículo */}
          {despesa.veiculo && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Car className="w-5 h-5 mr-2" />
                Veículo
              </h2>
              <div className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Placa</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">
                    {despesa.veiculo.placa}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Modelo</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {despesa.veiculo.marca} {despesa.veiculo.modelo}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Ano</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {despesa.veiculo.ano_fabricacao}
                  </dd>
                </div>
              </div>
            </div>
          )}

          {/* Motorista */}
          {despesa.motorista && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2" />
                Motorista
              </h2>
              <div className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Nome</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">
                    {despesa.motorista.nome}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">CPF</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {despesa.motorista.cpf}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">CNH</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {despesa.motorista.cnh}
                  </dd>
                </div>
              </div>
            </div>
          )}

          {/* Fornecedor */}
          {despesa.fornecedor && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Fornecedor
              </h2>
              <div className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Nome</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">
                    {despesa.fornecedor}
                  </dd>
                </div>
                {despesa.documento_fornecedor && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Documento</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {despesa.documento_fornecedor}
                    </dd>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Informações do Sistema */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Sistema
            </h2>
            <div className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Criado em</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(despesa.created_at)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Atualizado em</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(despesa.updated_at)}
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DespesaDetail;
